import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Card, CardBody } from 'reactstrap';
import { type SurveyQuestionDefinition } from 'views/CustomSurveys/formatSurveyAnswer';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { getQuestionTypeName } from './consts';

export interface OverviewQuestion {
  id: number;
  uuid: string;
  order: number;
  question: SurveyQuestionDefinition;
  answer_count: number;
}

interface Props {
  questions: OverviewQuestion[];
  onSelectQuestion: (uuid: string) => void;
}

const CustomSurveyResponsesQuestionsTab: FC<Props> = ({
  questions,
  onSelectQuestion,
}) => {
  const { formatMessage } = useIntl();

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.responses.col.question',
          defaultMessage: 'Question',
        }),
        field: 'question',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.responses.col.type',
          defaultMessage: 'Type',
        }),
        field: 'type',
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.responses.col.answers',
          defaultMessage: 'Answers',
        }),
        field: 'answer_count',
        className: 'col-1 text-end',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.responses.col.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'text-end text-nowrap',
        sortable: false,
      },
    ],
    [formatMessage]
  );

  const rows = useMemo(
    () =>
      questions.map((q) => ({
        id: q.id,
        question: <RichTextViewer model={q.question.label} expanded={true} />,
        type: getQuestionTypeName(q.question.type, formatMessage),
        answer_count: q.answer_count,
        actions: (
          <Button
            color="light"
            size="sm"
            onClick={() => onSelectQuestion(q.uuid)}
          >
            <FormattedMessage
              id="app.views.administration.custom_surveys.responses.view"
              defaultMessage="View"
            />
          </Button>
        ),
      })),
    [questions, onSelectQuestion, formatMessage]
  );

  if (questions.length === 0) {
    return (
      <Card>
        <CardBody className="text-muted text-center">
          <FormattedMessage
            id="app.views.administration.custom_surveys.responses.no_questions"
            defaultMessage="No questions in this instance."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <FilterablePeopleTable
      columns={columns}
      rows={rows}
      hideFilters={true}
      hideExportButton={true}
      hideHeader={true}
    />
  );
};

export default CustomSurveyResponsesQuestionsTab;
